import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  input
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogConfig } from '@angular/material/dialog';
import { WcgButtonComponent, WcgTableData } from '@wcg/frontend-framework';
import {
  GRID_LABELS,
  GridType,
  GridView,
  GridViewDialogData,
  GridViewDialogService,
  GridViewFacadeService,
  IsObjectEmptyPipe,
  ViewActionType,
} from '@data-access/grid-views';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        WcgButtonComponent,
        NgClass,
        MatMenuModule,
        IsObjectEmptyPipe,
    ],
    providers: [GridViewDialogService],
    selector: 'wcg-create-grid-view',
    styleUrls: ['./create-gird-view.component.scss'],
    template: `
    @if (isTableColumnChanged()()) {
      <wcg-button color="tertiary" size="slim">
        <span
          [ngClass]="{
            'create-grid-view__disable-btn': isDisabled(),
            'create-grid-view__not-default-view':
              selectedViewOption().label !== 'Default View',
          }"
          (click)="openSaveDialog(viewActionLabels.save, $event)"
          >{{ label() }}</span
        >
        @if (selectedViewOption().label !== 'Default View') {
          <span
            [matMenuTriggerFor]="updateView"
            (click)="$event.stopPropagation(); matIconUp.set(!matIconUp())"
            class="relative top-1"
          >
            @if (!matIconUp()) {
              <mat-icon class="grid-view-drop-down__icon"
                >keyboard_arrow_down</mat-icon
              >
            }
            @if (matIconUp()) {
              <mat-icon class="grid-view-drop-down__icon"
                >keyboard_arrow_up</mat-icon
              >
            }
          </span>
        }
      </wcg-button>
    }
    <mat-menu
      #updateView="matMenu"
      yPosition="below"
      (closed)="matIconUp.set(!matIconUp())"
    >
      <!-- @Todo -->
      <button
        mat-menu-item
        (click)="openSaveDialog(viewActionLabels.createNewView, $event)"
        [ngClass]="{ 'create-grid-view__disable-btn': isDisabled() }"
      >
        Create new view
      </button>
      @if (
        selectedViewOption().label !== 'Default View' &&
        (currentGridView() | isObjectEmpty) &&
        isTableColumnChanged()()
      ) {
        <button
          mat-menu-item
          (click)="openSaveDialog(viewActionLabels.updateExistingView, $event)"
        >
          Update existing view
        </button>
      }
    </mat-menu>
  `
})
export class CreateGridViewComponent {
  private _gvFacadeService = inject(GridViewFacadeService);
  private readonly _gridViewDialogService = inject(
    GridViewDialogService<
      GridView<Pick<WcgTableData, 'headers' | 'filters' | 'sortBy'>>
    >,
  );

  matIconUp = signal<boolean>(false);

  viewActionLabels = GRID_LABELS;

  selectedViewOption = this._gvFacadeService.$selectedView;
  currentGridView = this._gvFacadeService.$currentGridView;

  readonly label = input<string>(GRID_LABELS.save);
  readonly data = input.required<Pick<WcgTableData, 'headers' | 'filters' | 'sortBy'>>();
  readonly gridViewType = input.required<GridType>();
  readonly isDisabled = input(false);
  readonly isTableColumnChanged = input(signal<boolean>(false));

  openSaveDialog(viewAction: string, $event: Event): void {
    $event.stopPropagation();
    const config: MatDialogConfig = {
      autoFocus: false,
      width: '550px',
    };

    const getViewAction = (): {
      viewAction: ViewActionType;
      btnLabel: string;
      header: string;
    } => {
      switch (viewAction) {
        case GRID_LABELS.createNewView:
          return {
            btnLabel: GRID_LABELS.createNewView,
            header: GRID_LABELS.createNewView,
            viewAction: GRID_LABELS.create,
          };
        case GRID_LABELS.updateExistingView:
          return {
            btnLabel: GRID_LABELS.updateView,
            header: GRID_LABELS.updateExistingView,
            viewAction: GRID_LABELS.update,
          };
        default:
          return {
            btnLabel: GRID_LABELS.saveView,
            header: GRID_LABELS.saveView,
            viewAction: GRID_LABELS.save,
          };
      }
    };

    const getGridProps = () => {
      const gridProps = {
        description: '',
        grid_type: this.gridViewType(),
      };
      if (viewAction === GRID_LABELS.updateExistingView) {
        return {
          ...gridProps,
          id: this.currentGridView().id,
          name: this.currentGridView().name,
        } as Omit<GridView<unknown>, 'data'> | undefined;
      }
      return gridProps as Omit<GridView<unknown>, 'data'> | undefined;
    };

    const dialogConfig: GridViewDialogData = {
      config: config,
      data: {
        filters: this._gvFacadeService.$customFilters().filters,
        headers: JSON.stringify(this.data()?.headers),
        sortBy: this._gvFacadeService.$customFilters().sortBy,
      },
      gridBtnLabel: getViewAction().btnLabel,
      gridHeader: getViewAction().header,
      gridProps: getGridProps(),
      viewAction: getViewAction().viewAction,
    };

    this._gridViewDialogService.open(dialogConfig, 'Save');
  }
}
